import React, {useCallback} from "react";
import {Box, ToggleButton, ToggleButtonGroup} from "@mui/material";
import Typography from "@mui/material/Typography";
import {Application, formatApplication} from "./Application";
import {useQuery} from "react-query";
import {getApplicationUserCount} from "./ApplicationsApi";
import {useAxios} from "../../shared/technical/rest/AxiosProvider";
import {useTranslation} from "react-i18next";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import {useSubPages} from "../../shared/components/page/SubPageProvider";
import {palette} from "../../shared/technical/style/theme";
import {mayEditApplication} from "./permissions";
import {useAccount} from "../users/AccountProvider";

export enum ApplicationSubPages {
  INFORMATION = "information",
  TRANSLATIONS = "translations",
  TERMS_AND_CONDITIONS = "terms-and-conditions",
  ROLES = "roles",
  GROUPS = "groups",
  LICENSES = "licenses"
}

const buttonSXProps = {
  my: 1,
  textTransform: "none",
  color: "primary.main",
  backgroundColor: "background.light",
};
const buttonLabelSXProps = {ml: 1, mr: "auto"};
const buttonGroupSXProps = {
  width: "100%",
  "& .MuiButtonBase-root.MuiToggleButton-root.Mui-selected": {
    color: palette.primary,
    backgroundColor: palette.primaryTransparent
  }
};

export function ApplicationMenu(props: {
  application: Application
}) {
  const {application} = props;
  const {t} = useTranslation();
  const axios = useAxios();
  const account = useAccount();
  const {selectedSubPage, selectSubPage} = useSubPages();
  const {data: userCount} = useQuery('applicationUserCount_' + props.application.id!!,
    () => application.id ? getApplicationUserCount(axios, application.id!!) : Promise.resolve(0), {});

  const onToggleButtonGroupChange = useCallback((event: React.MouseEvent<HTMLElement>, newPage: string | null,) => {
    newPage && selectSubPage(newPage);
  }, [selectSubPage]);

  const additionalSubPagesEnabled = !!application.id && mayEditApplication(account, application);

  return (
    <Box sx={{height: "fit-content", borderColor: "gray", borderStyle: "solid", borderWidth: 1, borderRadius: 2, padding: 3}}>
      <Typography variant={"h1"} sx={{mt: 3, mx: "auto", width: "fit-content"}}>{formatApplication(application)}</Typography>
      <Box sx={{width: "100%", height: {lg: "50px"}}}/>
      <Typography variant={"subtitle1"} sx={{mt: 3, mx: "auto", width: "fit-content"}}>
        {userCount?.toString() + " " + t('Application.userCount')}
      </Typography>
      <Box sx={{width: "100%", height: {lg: "100px"}}}/>
      <ToggleButtonGroup
        value={selectedSubPage}
        onChange={onToggleButtonGroupChange}
        exclusive
        orientation={"vertical"}
        aria-label="application menu"
        sx={buttonGroupSXProps}
      >
        <ToggleButton sx={buttonSXProps} value={ApplicationSubPages.INFORMATION}>
          <InfoOutlinedIcon/><Box sx={buttonLabelSXProps}>{t('ApplicationPage.subPageInformation')}</Box>
        </ToggleButton>
        <ToggleButton sx={buttonSXProps} value={ApplicationSubPages.TRANSLATIONS} disabled={!additionalSubPagesEnabled}>
          <TranslateOutlinedIcon/><Box sx={buttonLabelSXProps}>{t('ApplicationPage.subPageTranslations')}</Box>
        </ToggleButton>
        <ToggleButton sx={buttonSXProps} value={ApplicationSubPages.TERMS_AND_CONDITIONS} disabled={!additionalSubPagesEnabled}>
          <ArticleOutlinedIcon/><Box sx={buttonLabelSXProps}>{t('ApplicationPage.subPageTermsAndConditions')}</Box>
        </ToggleButton>
        <ToggleButton sx={buttonSXProps} value={ApplicationSubPages.ROLES} disabled={!additionalSubPagesEnabled}>
          <EngineeringOutlinedIcon/><Box sx={buttonLabelSXProps}>{t('ApplicationPage.subPageRoles')}</Box>
        </ToggleButton>
        <ToggleButton sx={buttonSXProps} value={ApplicationSubPages.LICENSES} disabled={!additionalSubPagesEnabled}>
          <SellOutlinedIcon/><Box sx={buttonLabelSXProps}>{t('ApplicationPage.subPageLicenses')}</Box>
        </ToggleButton>
        <ToggleButton sx={buttonSXProps} value={ApplicationSubPages.GROUPS} disabled={!additionalSubPagesEnabled}>
          <GroupsOutlinedIcon/><Box sx={buttonLabelSXProps}>{t('ApplicationPage.subPageGroups')}</Box>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}
